import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, User, ArrowRight } from "lucide-react";
import PageHero, { usePageTitle } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CONTACT, INDUSTRIES, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-oxford/15 px-4 py-3.5 text-sm text-carbon placeholder:text-steel/70 transition-colors duration-300 focus:border-gold focus:outline-none";

const INITIAL = { nombre: "", empresa: "", email: "", telefono: "", industria: "", ayuda: "", mensaje: "" };

export default function Contacto() {
  usePageTitle("Contacto | Hablemos de tu próxima oportunidad — BRIK");
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Solicitud enviada. Te contactaremos muy pronto.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("No pudimos enviar tu solicitud. Intenta de nuevo o escríbenos directamente.");
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: User, label: "Contacto", value: CONTACT.founder, sub: CONTACT.role },
    { icon: Phone, label: "Cel", value: CONTACT.phone, href: `tel:${CONTACT.phoneHref}`, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
    { icon: Globe, label: "Website", value: CONTACT.website },
    { icon: MapPin, label: "Ubicación", value: CONTACT.location },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contacto"
        titleLines={["Hablemos de tu", "próxima oportunidad."]}
        description="Cuéntanos qué estás buscando transformar y encontremos juntos la mejor forma de generar valor."
      />

      <section data-testid="contact-section" className="bg-bone">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="h-px w-10 bg-gold" />
                <span className="eyebrow">Datos de contacto</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight text-oxford leading-tight">
                Cercanía ejecutiva, desde el primer mensaje.
              </h2>
            </Reveal>
            <div className="mt-10 border-t border-oxford/10 divide-y divide-oxford/10">
              {info.map((item, i) => (
                <Reveal key={item.label} delay={0.12 + i * 0.07}>
                  <div className="flex items-start gap-5 py-5">
                    <span className="mt-0.5 flex h-10 w-10 items-center justify-center bg-oxford shrink-0">
                      <item.icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-display text-[10px] font-semibold tracking-[0.25em] uppercase text-steel">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} data-testid={item.testid} className="mt-1 inline-block text-sm font-medium text-oxford hover:text-gold transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-oxford">{item.value}</p>
                      )}
                      {item.sub && <p className="text-xs text-steel mt-0.5">{item.sub}</p>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="relative bg-white border border-oxford/10 p-8 lg:p-12"
              >
                <span className="absolute top-0 left-0 h-[2px] w-24 bg-gold" />
                <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-oxford">Envía tu solicitud</h2>
                <p className="mt-2 text-sm text-carbon/70">Respondemos personalmente cada mensaje.</p>

                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nombre" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">Nombre *</label>
                    <input id="nombre" name="nombre" data-testid="contact-nombre" required value={form.nombre} onChange={update} className={inputCls} placeholder="Tu nombre" />
                  </div>
                  <div>
                    <label htmlFor="empresa" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">Empresa</label>
                    <input id="empresa" name="empresa" data-testid="contact-empresa" value={form.empresa} onChange={update} className={inputCls} placeholder="Nombre de tu empresa" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">Correo electrónico *</label>
                    <input id="email" name="email" type="email" data-testid="contact-email-input" required value={form.email} onChange={update} className={inputCls} placeholder="nombre@empresa.com" />
                  </div>
                  <div>
                    <label htmlFor="telefono" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">Teléfono</label>
                    <input id="telefono" name="telefono" type="tel" data-testid="contact-telefono" value={form.telefono} onChange={update} className={inputCls} placeholder="+52 ..." />
                  </div>
                  <div>
                    <label htmlFor="industria" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">Industria</label>
                    <select id="industria" name="industria" data-testid="contact-industria" value={form.industria} onChange={update} className={`${inputCls} appearance-none`}>
                      <option value="">Selecciona una opción</option>
                      {INDUSTRIES.map((i) => (
                        <option key={i.title} value={i.title}>{i.title}</option>
                      ))}
                      <option value="Otra">Otra</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ayuda" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">¿En qué podemos ayudarte?</label>
                    <select id="ayuda" name="ayuda" data-testid="contact-ayuda" value={form.ayuda} onChange={update} className={`${inputCls} appearance-none`}>
                      <option value="">Selecciona una opción</option>
                      {SERVICES.map((s) => (
                        <option key={s.title} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Diagnóstico general">Diagnóstico general</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="mensaje" className="block font-display text-[10px] font-semibold tracking-[0.22em] uppercase text-oxford/70 mb-2">Mensaje *</label>
                    <textarea id="mensaje" name="mensaje" data-testid="contact-mensaje" required rows={5} value={form.mensaje} onChange={update} className={`${inputCls} resize-none`} placeholder="Cuéntanos qué estás buscando transformar..." />
                  </div>
                </div>

                <button type="submit" data-testid="contact-submit" disabled={sending} className="btn-gold mt-8 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed">
                  {sending ? "Enviando..." : "Enviar solicitud"} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
